import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axiosInstance, { clearAuthData, isJwtAuthFailure } from "../../components/content/axiosConfig";
import { enqueueSnackbar } from "notistack";
import Swal from 'sweetalert2';
import Preloader from "../../components/custom/preLoader";
import ListPagination from "../../components/custom/ListPagination";
import useServerPaginatedList from "../../hooks/useServerPaginatedList";
import { openAdminPanelAsUser } from "../../utils/adminHandoff";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast.js";
import { formatDisplayDateTime as formatDateTime } from "../../utils/formatDisplayDate";

const Staff = () => {
  const { id } = useParams();
  const [loggingInId, setLoggingInId] = useState(null);

  const fetchStaffPage = useCallback(async ({ search, page, limit }) => {
    try {
      return await axiosInstance.get(`api/User/GetAllStaff/${id}`, {
        params: { search, page, limit },
      });
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return { data: { data: [], total: 0 } };
      }
      let errorMessage = "An error occurred.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 3000,
      });
      throw error;
    }
  }, [id]);

  const {
    rows: staffs,
    totalCount,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    isLoading,
    entriesPerPage,
    refetch,
  } = useServerPaginatedList(fetchStaffPage, { enabled: !!id });

  useEffect(() => {
    if (!id) {
      clearAuthData();
      window.location.href = "/user/login";
    }
  }, [id]);

  const loginToAdminPanel = async (staffId) => {
    if (loggingInId != null) return;
    setLoggingInId(staffId);
    try {
      const res = await axiosInstance.post("/api/SuperUser/LoginAsAdmin", { staffId });
      openAdminPanelAsUser(res.data);
      showSuccessToast("Opening Admin panel…");
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return;
      }
      showErrorToast(error.response?.data?.message || "Could not open Admin panel");
    } finally {
      setLoggingInId(null);
    }
  };

  const updateStatus = async (staffId) => {
    try {
      await axiosInstance.post(`api/User/ChangeStatus/${staffId}`);
      await refetch();
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
      }
      console.log(error);
    }
  };

  const DeleteUser = async (staffId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This member will be removed from the organization.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete("api/User/DeleteStaff", {
          params: { id: staffId, fisherID: id },
        });
        enqueueSnackbar("Member deleted successfully", {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 3000,
        });
        await refetch();
      } catch (error) {
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          return;
        }
        const errorMessage = error.response?.data?.message || "Failed to delete member";
        enqueueSnackbar(errorMessage, {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 4000,
        });
        console.error("Error deleting staff:", error);
      }
    }
  };

  if (isLoading && staffs.length === 0) {
    return <Preloader />;
  }

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ul className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">Staff</li>
          </ul>
          <h1 className="page-header mb-0">Staff</h1>
        </div>
        <div className="ms-auto" />
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active" id="allTab">
            <div className="input-group mb-3">
              <div className="flex-fill position-relative">
                <div className="input-group">
                  <div
                    className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                    style={{ zIndex: 10 }}
                  >
                    <i className="fa fa-search opacity-5"></i>
                  </div>
                  <input
                    type="text"
                    className="form-control px-35px bg-light"
                    placeholder="Search Staff..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="table-responsive mb-3">
              <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Created At</th>
                    <th>Actions </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-5">
                        Loading…
                      </td>
                    </tr>
                  ) : staffs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-5">
                        No staff members found for this account.
                      </td>
                    </tr>
                  ) : (
                    staffs.map((staff) => (
                      <tr key={staff.id}>
                        <td>{staff.FullName}</td>
                        <td>{staff.Email}</td>
                        <td>
                          {staff.IsAdmin ? (
                            <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                              <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                              Org-wide
                            </span>
                          ) : staff.IsFisherOwner ? (
                            <span className="badge border border-primary text-primary px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                              <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                              Owner
                            </span>
                          ) : staff.isAssociatedStaff === 1 ||
                            staff.isAssociatedStaff === "1" ||
                            staff.IsIndependentScientist ? (
                            <span className="badge border border-info text-info px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                              <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                              Scientist
                            </span>
                          ) : (
                            <span className="badge border border-yellow text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                              <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                              Pond team
                            </span>
                          )}
                        </td>
                        <td onClick={() => { updateStatus(staff.id) }}>
                          {staff.Status ? (
                            <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                              <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                              Active
                            </span>
                          ) : (
                            <span className="badge border border-danger text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                              <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                              Pending
                            </span>
                          )}
                        </td>
                        <td>{formatDateTime(staff.createdAt)}</td>
                        <td>
                          <span>
                            <button
                              type="button"
                              className="btn btn-link p-0 border-0 text-primary"
                              title="Open Admin panel as this user"
                              disabled={loggingInId != null}
                              onClick={() => loginToAdminPanel(staff.id)}
                            >
                              <i className="fa fa-sign-in fs-13px fa-fw me-5px" />
                            </button>
                          </span>
                          <span>
                            <Link to={"/UpdateStaff/" + staff.id}>
                              {" "}
                              <i className="fa fa-pencil fs-13px fa-fw me-5px"></i>
                            </Link>
                          </span>
                          &nbsp;&nbsp;
                          <span>
                            <i
                              onClick={() => DeleteUser(staff.id)}
                              className="fa fa-trash fs-13px fa-fw me-5px"
                            ></i>
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <ListPagination
              currentPage={currentPage}
              totalCount={totalCount}
              entriesPerPage={entriesPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Staff;
